import React, { useEffect, useMemo, useRef, useState } from "react";
import { Spinner, Stack } from "react-bootstrap";
import { useSelector } from "react-redux";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService, GetWorkRequestsByPriorityResponse } from "src/services/Firebase/data";
import { colors } from "src/styles";
import styled from "styled-components";
import * as d3 from "d3";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

interface WidgetProps {
  caption: string;
}

interface PriorityData {
  priorityId: number;
  priorityName: string;
  priorityColor: string;
  count: number;
}

const BROOKEFIELD_CLIENT_ID = "BROOKEFIELD";

const brookefieldPriorityData = {
  widgetsData: {
    workRequestsPriorityStatistics: [
      { priorityId: 1, priorityName: "Critical", priorityColor: "#d32f2f", count: 28 },
      { priorityId: 2, priorityName: "High", priorityColor: "#f57c00", count: 45 },
      { priorityId: 3, priorityName: "Medium", priorityColor: "#fbc02d", count: 67 },
      { priorityId: 4, priorityName: "Low", priorityColor: "#388e3c", count: 92 },
    ],
  },
} as GetWorkRequestsByPriorityResponse;

const WorkOrderPriorityChart = ({ caption }: WidgetProps) => {
  const { id: loggedInUserId, isManager, clientId } = useSelector(getUserData);
  const [data, setData] = React.useState<GetWorkRequestsByPriorityResponse>();
  const [dateFrom, setDateFrom] = useState<Date | null>(moment().subtract(180, "days").toDate());
  const [dateTo, setDateTo] = useState<Date | null>(new Date());
  const svgRef = useRef<SVGSVGElement>(null);
  const isBrookefieldClient = (clientId ?? "").trim().toUpperCase() === BROOKEFIELD_CLIENT_ID;

  const priorityData: PriorityData[] = useMemo(() => {
    if (!data?.widgetsData?.workRequestsPriorityStatistics) {
      return [];
    }
    return data.widgetsData.workRequestsPriorityStatistics;
  }, [data]);

  const total = useMemo(() => {
    return priorityData.reduce((acc, item) => acc + item.count, 0);
  }, [priorityData]);

  useEffect(() => {
    if (isBrookefieldClient) {
      setData(brookefieldPriorityData);
      return;
    }

    if (!dateFrom || !dateTo) return;

    const fetchData = async () => {
      const fromDate = moment(dateFrom).format("YYYY-MM-DD");
      const toDate = moment(dateTo).format("YYYY-MM-DD");
      const { data } = await FirebaseDataService.getWorkRequestsByPriority(loggedInUserId, isManager, fromDate, toDate);
      setData(data);
    };

    fetchData();
  }, [isBrookefieldClient, dateFrom, dateTo, loggedInUserId, isManager]);

  useEffect(() => {
    if (!svgRef.current || priorityData.length === 0) return;

    const width = 220;
    const height = 220;
    const radius = Math.min(width, height) / 2;

    // Clear previous content
    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", [-width / 2, -height / 2, width, height])
      .attr("style", "max-width: 100%; height: auto;");

    // Create pie layout
    const pie = d3
      .pie<PriorityData>()
      .value((d) => d.count)
      .sort(null);

    // Create arc generator
    const arc = d3
      .arc<d3.PieArcDatum<PriorityData>>()
      .innerRadius(0)
      .outerRadius(radius - 16);

    // Create arc for labels (slightly outside)
    const labelArc = d3
      .arc<d3.PieArcDatum<PriorityData>>()
      .innerRadius(radius - 24)
      .outerRadius(radius - 24);

    // Create pie slices
    const arcs = svg.selectAll(".arc").data(pie(priorityData)).enter().append("g").attr("class", "arc");

    // Add paths (slices) with sweep animation
    arcs
      .append("path")
      .attr("fill", (d) => d.data.priorityColor)
      .attr("stroke", "white")
      .attr("stroke-width", 2)
      .style("opacity", 0.9)
      .on("mouseover", function () {
        d3.select(this).style("opacity", 1).style("cursor", "pointer");
      })
      .on("mouseout", function () {
        d3.select(this).style("opacity", 0.9);
      })
      .transition()
      .duration(800)
      .ease(d3.easeCubicOut)
      .attrTween("d", (d) => {
        const interpolate = d3.interpolate({ startAngle: d.startAngle, endAngle: d.startAngle }, d);
        return (t) => arc(interpolate(t)) as string;
      });

    // Add percentage labels on slices — fade in after sweep
    arcs
      .append("text")
      .attr("transform", (d) => {
        const pos = labelArc.centroid(d);
        return `translate(${pos})`;
      })
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .attr("font-weight", "600")
      .attr("fill", "white")
      .style("text-shadow", "1px 1px 2px rgba(0,0,0,0.6)")
      .style("opacity", 0)
      .text((d) => {
        const percentage = ((d.data.count / total) * 100).toFixed(1);
        return `${percentage}%`;
      })
      .transition()
      .delay(750)
      .duration(250)
      .style("opacity", 1);
  }, [priorityData, total]);

  return (
    <WidgetContainer>
      <Stack gap={3}>
        <HeaderRow>
          <h1 style={{ fontWeight: 600, margin: 0 }}>{caption}</h1>
          {isBrookefieldClient && <StaticDataBadge>Static Data</StaticDataBadge>}
          <DateFilterContainer>
            <DateFilterLabel>From:</DateFilterLabel>
            <StyledDatePicker
              selected={dateFrom}
              onChange={(date: Date) => setDateFrom(date)}
              dateFormat="MM/dd/yyyy"
              placeholderText="Start Date"
              maxDate={dateTo || new Date()}
            />
            <DateFilterLabel>To:</DateFilterLabel>
            <StyledDatePicker
              selected={dateTo}
              onChange={(date: Date) => setDateTo(date)}
              dateFormat="MM/dd/yyyy"
              placeholderText="End Date"
              minDate={dateFrom}
              maxDate={new Date()}
            />
          </DateFilterContainer>
        </HeaderRow>
        {data ? (
          <ChartContainer>
            <svg ref={svgRef}></svg>
            <Legend>
              {priorityData.map((item, index) => (
                <LegendItem key={index}>
                  <ColorBox $color={item.priorityColor} />
                  <LegendText>
                    {item.priorityName} ({item.priorityId}): <strong>{item.count}</strong> (
                    {((item.count / total) * 100).toFixed(1)}%)
                  </LegendText>
                </LegendItem>
              ))}
              <TotalRow>
                <strong>Total: {total}</strong>
              </TotalRow>
            </Legend>
          </ChartContainer>
        ) : (
          <Spinner animation="border" />
        )}
      </Stack>
    </WidgetContainer>
  );
};

export default WorkOrderPriorityChart;

const WidgetContainer = styled.div`
  display: flex;
  min-width: 0;
  border: 1px solid ${colors.grey.light11};
  border-top: 4px solid ${colors.kleenway.green};
  padding: 16px;
  border-radius: 6px;
`;

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  justify-content: center;
  width: 100%;

  svg {
    width: 220px;
    height: 220px;
    flex-shrink: 0;
  }
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  min-width: 0;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
`;

const ColorBox = styled.div<{ $color: string }>`
  width: 14px;
  height: 14px;
  background-color: ${({ $color }) => $color};
  border-radius: 3px;
  flex-shrink: 0;
`;

const LegendText = styled.span`
  color: ${colors.grey.dark};
  word-break: break-word;
`;

const TotalRow = styled.div`
  margin-top: 6px;
  padding-top: 8px;
  border-top: 2px solid ${colors.grey.light11};
  font-size: 12px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const StaticDataBadge = styled.span`
  background-color: ${colors.grey.light11};
  color: ${colors.grey.dark};
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const DateFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const DateFilterLabel = styled.span`
  font-size: 12px;
  color: ${colors.grey.dark};
  font-weight: 500;
`;

const StyledDatePicker = styled(DatePicker)`
  padding: 4px 8px;
  border: 1px solid ${colors.grey.light11};
  border-radius: 4px;
  font-size: 12px;
  width: 110px;
  outline: none;

  &:focus {
    border-color: ${colors.kleenway.green};
  }

  &::placeholder {
    color: ${colors.grey.light10};
  }
`;
